import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAuth } from '@/hooks/use-auth';
import { useReleaseNotes } from '@/lib/releaseNotes/useReleaseNotes';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { WhatsNewDialog } from './WhatsNewDialog';
const ReleaseNotesContext = createContext(null);
// The current release auto-opens once per user only on the main landing
// surfaces — never over the viewer, the report editor, or a public share link.
// Match the teleconsultation worklist exactly: `startsWith('/teleconsultation')`
// would also capture the report editor (`/teleconsultation/report/:caseId`) and
// billing-usage, popping the modal over a report the user is actively writing.
export function isLandingRoute(path) {
    return path === '/' || path === '/study/list' || path === '/teleconsultation';
}
/**
 * Owns the "What's new" modal so it can be opened either automatically (once per
 * user when a new release ships) or on demand from anywhere via
 * useReleaseNotesModal() — e.g. the Help menu's "Release notes" item.
 */
export function ReleaseNotesProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const { latest, hasUnseen, markSeen } = useReleaseNotes();
    const [location, setLocation] = useLocation();
    const [manualOpen, setManualOpen] = useState(false);
    const open = useCallback(() => setManualOpen(true), []);
    const value = useMemo(() => ({ open }), [open]);
    // Auto-open is derived (no setState-in-effect). Manual open works anywhere.
    const autoOpen = isAuthenticated && hasUnseen && isLandingRoute(location);
    const isOpen = !!latest && (autoOpen || manualOpen);
    const close = () => {
        markSeen();
        setManualOpen(false);
    };
    return (_jsxs(ReleaseNotesContext.Provider, { value: value, children: [children, latest && (_jsx(WhatsNewDialog, { note: latest, open: isOpen, onClose: close, onViewAll: () => {
                    markSeen();
                    setManualOpen(false);
                    setLocation('/whats-new');
                } }))] }));
}
/** Opens the "What's new" modal. No-op when used outside the provider. */
export function useReleaseNotesModal() {
    return useContext(ReleaseNotesContext) ?? { open: () => { } };
}
